import { cn } from '../../utils';
import { DecimalInput } from '../inputs/DecimalInput';
import { CurrencySelector } from '../selects/CurrencySelector';
import { TabSwitcher } from '../TabSwitcher';

export interface AmountRangeFilterValue {
  /** Lower bound as a decimal string */
  from?: string;
  /** Upper bound as a decimal string */
  to?: string;
  /** Id of the chosen VAT option */
  vat?: string;
  /** ISO 4217 code; omitted means no currency chosen */
  currency?: string;
}

export interface AmountRangeFilterVatOption {
  id: string;
  label: string;
}

export interface AmountRangeFilterEditorProps {
  value: AmountRangeFilterValue | undefined;
  onChange: (next: AmountRangeFilterValue) => void;
  /** Segmented VAT toggle; omitted hides it */
  vatOptions?: AmountRangeFilterVatOption[];
  /** Currency row for "no currency chosen" */
  unselectedCurrencyLabel: string;
  fromPlaceholder: string;
  toPlaceholder: string;
  /** Caption under the currency */
  hint?: string;
  className?: string;
  testId: string;
}

const orUndefined = (text: string): string | undefined => (text === '' ? undefined : text);

/** Amount filter editor: from/to inputs, optional VAT toggle and a currency. */
export const AmountRangeFilterEditor = ({
  value,
  onChange,
  vatOptions,
  unselectedCurrencyLabel,
  fromPlaceholder,
  toPlaceholder,
  hint,
  className,
  testId
}: AmountRangeFilterEditorProps) => {
  const current = value ?? {};
  const update = (patch: AmountRangeFilterValue) => onChange({ ...current, ...patch });

  return (
    <div data-testid={testId} className={cn('flex flex-col gap-sm', className)}>
      {vatOptions && vatOptions.length > 0 && (
        <TabSwitcher
          items={vatOptions}
          activeTab={current.vat ?? vatOptions[0].id}
          onTabChange={vat => update({ vat })}
          testId={`${testId}-vat`}
        />
      )}
      <div className='grid grid-cols-2 gap-sm'>
        <DecimalInput
          height='sm'
          value={current.from}
          placeholder={fromPlaceholder}
          onChange={from => update({ from: orUndefined(from) })}
          testId={`${testId}-from`}
        />
        <DecimalInput
          height='sm'
          value={current.to}
          placeholder={toPlaceholder}
          onChange={to => update({ to: orUndefined(to) })}
          testId={`${testId}-to`}
        />
      </div>
      <CurrencySelector
        height='sm'
        value={current.currency}
        unselectedOptionLabel={unselectedCurrencyLabel}
        placeholder={unselectedCurrencyLabel}
        onChange={code => update({ currency: orUndefined(code) })}
        testId={`${testId}-currency`}
      />
      {hint && <p className='text-xs text-foreground-muted'>{hint}</p>}
    </div>
  );
};

AmountRangeFilterEditor.displayName = 'AmountRangeFilterEditor';
